import { useEffect, useMemo, useCallback } from 'react';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { useQCQueriesSlice } from '@restore/qcQueries/qcQueries.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { QueriesFilters } from 'src/components/Queries/QueriesFilters';
import { useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router';
import { routesPath } from '@router/routes';
import { useQueriesColumns } from '@components/Queries/useQueriesColumns';
import { useQCQueryHandlers } from '@components/Queries/useQCQueryHandlers';
import { QueryItem } from '@restore/queries/queries.types';
import {
  useMakePagination,
  usePaginationAndSortSlice,
} from '@restore/paginationAndSort/paginationAndSort.slice';
import { ListName } from '@restore/paginationAndSort/paginationAndSort.types';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';

export const QCQueries = () => {
  const navigate = useNavigate();
  const [loading, data, fetchQCQueries, filters, setFilters, currentQueryUpdating, updateCurrentQuery] =
    useQCQueriesSlice((state, actions) => [
      state.loading,
      state.data,
      actions.fetchQCQueries,
      state.filters,
      actions.setFilters,
      state.currentQueryUpdating,
      actions.updateCurrentQuery,
    ]);
  const contextData = useClinicalTrialContextSlice(state => state.data);
  const ctId = contextData?.configuration?.id;
  useBreadcrumbs([['QC Queries', 'qcQueries']], [ctId!]);
  const { handlers } = useQCQueryHandlers({
    ctId: ctId!,
    updating: currentQueryUpdating,
    updateCurrentQuery,
  });
  const columns = useQueriesColumns(handlers);
  const tableData = useMemo(() => data?.content ?? [], [data]);

  const [pagState, pagActions] = usePaginationAndSortSlice((state, actions) => [
    state.lists.qcQueries,
    actions
  ]);

  const pagination = useMakePagination(
    'qcQueries' as ListName,
    !!tableData.length,
    () => fetchQCQueries(ctId!)
  );

  const onSort = useCallback((
    columnName: string,
    sortState?: string
  ) => {
    const newSortBy = sortState ? `${columnName},${sortState}` : undefined;
    pagActions.setSortBy({
      listName: 'qcQueries' as ListName,
      newSortBy
    });
    fetchQCQueries(ctId!);
  }, [pagActions, fetchQCQueries, ctId]);

  useEffect(() => {
    if (ctId) {
      pagActions.resetListStateByName({ listName: 'qcQueries' as ListName });
      setFilters({ filterQuery: 'visitId', filterValue: '' });
      fetchQCQueries(ctId);
    }
  }, [ctId, filters.status, filters.patientCode, pagActions, setFilters, fetchQCQueries]);

  return (
    <div>
      <div className='flex items-center justify-between py-4.5'>
        <h1>Queries</h1>
      </div>
      <QueriesFilters
        filters={filters}
        setFilters={setFilters}
        searchKey='patientCode'
      />
      <Table
        loading={loading}
        columns={columns}
        data={tableData}
        totalItems={data?.totalElements ?? 0}
        pagination={pagination}
        onSort={onSort}
        sortState={pagState.sortBy}
        onRowClick={row => {
          const clinicalTrialId = contextData?.configuration?.id;
          const queryId = (row as QueryItem)?.id;
          if (!clinicalTrialId || !queryId) return;
          const url = generatePath(routesPath.qcQuery, {
            id: clinicalTrialId,
            queryId,
          });
          navigate(url);
        }}
      />
    </div>
  );
};
